import { memo, useMemo } from 'react';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { Notification, channelLabel, formatCount, parseCountMessage } from '../../../../../services/NotificationService';
import { severityColor, typeColor } from './constants';

const TYPE_ICON: Record<string, typeof BarChartIcon> = {
  COUNT: BarChartIcon,
  EXPORT: FileDownloadIcon,
};

export interface NotificationItemProps {
  notification: Notification;
  read: boolean;
  onMarkAsRead: (id: string) => void;
  onArchive: (id: string) => void;
  onOpenDialog: (queryId: string, workspace?: string) => void;
}

export const NotificationItem = memo(function NotificationItem({
  notification,
  read,
  onMarkAsRead,
  onArchive,
  onOpenDialog,
}: NotificationItemProps) {
  const theme = useTheme();
  const typeKey = (notification.type || '').toUpperCase();
  const isCount = typeKey === 'COUNT';
  const TypeIcon = TYPE_ICON[typeKey] || NotificationsIcon;

  const parsed = useMemo(
    () => parseCountMessage(notification.message),
    [notification.message],
  );

  // Comptage : le total et le détail par canal sont mis en avant, la durée du traitement n'est pas affichée
  const total = isCount ? parsed?.count : null;
  const displayMsg = isCount ? notification.queryName : parsed?.message || notification.message;
  // Only error/warning/success are genuine alerts — color those by severity. Everything else
  // ("info", the default) is colored by notification type instead, so COUNT/EXPORT stay
  // visually distinct rather than all rendering as the same generic blue. Colors only ever tint
  // the small icon badge and the type label — never the card itself — to keep the list sober.
  const isAlert = notification.severity !== 'info';
  const color = isAlert ? severityColor(theme, notification.severity) : typeColor(theme, typeKey);

  return (
    <Box
      data-notification-id={notification.id}
      sx={{
        mb: 0.75,
        display: 'flex',
        gap: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: read ? 'grey.100' : 'grey.200',
        borderLeft: `3px solid ${read ? 'transparent' : color}`,
        bgcolor: read ? 'transparent' : 'grey.50',
        opacity: read ? 0.65 : 1,
        transition: 'opacity 0.2s, background-color 0.2s',
        overflow: 'hidden',
        p: 1,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.1),
          color,
        }}
      >
        <TypeIcon sx={{ fontSize: 14 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color, letterSpacing: 0.3, textTransform: 'uppercase' }}>
              {notification.type}
            </Typography>
            {!read && (
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            )}
            <Typography variant="caption" color="text.disabled" noWrap sx={{ fontSize: '0.65rem' }}>
              {new Date(notification.timestamp).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/Paris',
              })}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={read ? 'Marquer comme non lu' : 'Marquer comme lu'}>
              <IconButton
                size="small"
                onClick={() => onMarkAsRead(notification.id)}
                sx={{ color: 'text.disabled', p: 0.3 }}
              >
                {read
                  ? <VisibilityOffIcon sx={{ fontSize: 13 }} />
                  : <RemoveRedEyeIcon  sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Archiver">
              <IconButton
                size="small"
                onClick={() => onArchive(notification.id)}
                sx={{ p: 0.3, color: 'text.disabled' }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {displayMsg && (
          <Typography
            variant="body2"
            sx={{ mt: 0.2, fontSize: '0.78rem', fontWeight: read ? 400 : 500, color: 'text.primary', lineHeight: 1.35, overflowWrap: 'anywhere' }}
          >
            {displayMsg}
          </Typography>
        )}

        {total != null && (
          <Typography sx={{ mt: 0.2, fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
            {formatCount(total)}
            <Box component="span" sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>clients</Box>
          </Typography>
        )}

        {parsed?.listCount && parsed.listCount.length > 0 && (
          <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {parsed.listCount.map((c) => (
              <Chip
                key={c.channel}
                label={`${channelLabel(c.channel)} : ${formatCount(c.count)}`}
                size="small"
                sx={{
                  fontSize: '0.65rem',
                  height: 18,
                  bgcolor: 'grey.100',
                  color: 'text.secondary',
                  '& .MuiChip-label': { px: 0.8 },
                }}
              />
            ))}
          </Box>
        )}

        {isCount && notification.queryId && (
          <Button
            size="small"
            onClick={() => onOpenDialog(notification.queryId!, notification.workspace)}
            sx={{
              display: 'flex',
              ml: 'auto',
              mt: 0.5,
              fontSize: '0.68rem',
              py: 0.1, px: 0.8,
              minHeight: 0,
              color: 'text.secondary',
              textTransform: 'none',
              lineHeight: 1.3,
            }}
          >
            Voir les résultats
          </Button>
        )}
      </Box>
    </Box>
  );
});
